// What the account may change in the schema an app installed (stage B, audit of entities §4.1): names are the account's,
// the parts commands rely on are invariants. One rule for the server's refusals and the interface's disabled buttons.

import type { MetaIndex } from './meta-index.ts';
import type { FieldDef, Id, TermDef } from './model.ts';

export interface Lock { rename: boolean; archive: boolean; why: string | null }
const FREE: Lock = { rename: true, archive: true, why: null };

/** A taxonomy whose values commands set (a status with transitions): its app values are the commands' vocabulary. */
export const taxonomyManaged = (meta: MetaIndex, taxonomyId: string) =>
  meta.types().some((t) => meta.fields(t.id).some((f) => f.options.taxonomyId === taxonomyId && f.options.managed));

export function fieldLock(meta: MetaIndex, f: FieldDef): Lock {
  if (!f.system) return FREE;
  const type = meta.type(f.typeId);
  if (type?.titleField === f.key) return { rename: true, archive: false, why: 'Название записи — без него запись не показать' };
  if (f.options.managed) return { rename: true, archive: false, why: 'Поле заполняют действия приложения' };
  if (f.fieldType === 'relation') return { rename: true, archive: false, why: 'Связь нужна действиям приложения' };
  return { rename: true, archive: true, why: null };
}

export function termLock(meta: MetaIndex, t: TermDef): Lock {
  if (t.final) return { rename: true, archive: false, why: 'Итоговый этап воронки: «успех» и «отказ» нужны всегда' };
  if (t.system && taxonomyManaged(meta, t.taxonomyId)) return { rename: true, archive: false, why: 'Значение, которое ставят действия приложения' };
  return FREE;
}

/**
 * A taxonomy whose values are closed: an app sets them (a managed field) or they are the final stages of a
 * funnel. A value nobody has in such a taxonomy is an error of the row, not a new value made in silence —
 * the same rule as `termLock`, but for the whole list of values (platform П15).
 */
export function taxonomyLocked(meta: MetaIndex, taxonomyId: Id): boolean {
  if (taxonomyManaged(meta, taxonomyId)) return true;
  return meta.terms(taxonomyId).some((t) => t.final !== null);
}

/** A field that shows a taxonomy's values, with its section: «Сделки → Источник». Archived fields count — their records keep the values. */
/** `archived` — the field or its whole section is in the archive: its records keep the values, so it still holds the taxonomy. */
export interface TaxonomyUse { fieldId: Id; typeId: Id; label: string; archived: boolean; sectionArchived: boolean }

/**
 * Every field of every section, archived or not — an archived section included — that points to the taxonomy (decision 038 п.2).
 * A section in the archive keeps its records and comes back with them, so its fields count like live ones (review MiniMax P2-1).
 */
export function taxonomyUsage(meta: MetaIndex, taxonomyId: Id): TaxonomyUse[] {
  const out: TaxonomyUse[] = [];
  for (const o of meta.all.values()) {
    if (o.kind !== 'field' || o.options.taxonomyId !== taxonomyId) continue;
    const type = meta.type(o.typeId);
    const sectionArchived = !!type?.archivedAt;
    out.push({ fieldId: o.id, typeId: o.typeId, label: `${type?.nameMany ?? 'Раздел удалён'} → ${o.name}`, archived: !!o.archivedAt || sectionArchived, sectionArchived });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, 'ru'));
}

/**
 * What may happen to a taxonomy as a whole (decision 038 п.2): an app's one stays — its commands and fields rely on it; an own one goes to
 * the archive at any time and is deleted only while no field, even an archived one, points to it.
 */
export function taxonomyRemoval(meta: MetaIndex, taxonomyId: Id): { archive: boolean; delete: boolean; why: string | null; usedBy: string[] } {
  const t = meta.taxonomy(taxonomyId);
  const usedBy = taxonomyUsage(meta, taxonomyId).map((u) => (u.sectionArchived ? `${u.label} (раздел в архиве)` : u.archived ? `${u.label} (поле в архиве)` : u.label));
  if (!t || t.system) return { archive: false, delete: false, why: 'Таксономия приложения: на ней работают его поля и действия', usedBy };
  if (usedBy.length) return { archive: true, delete: false, why: `Используется в полях: ${usedBy.join('; ')}. Уберите таксономию в архив или сначала уберите эти поля`, usedBy };
  return { archive: true, delete: true, why: null, usedBy };
}
